import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Card, Spin, Alert as AntAlert, Button, Tag, Tooltip } from 'antd';
import { ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTheme } from '../../contexts/useTheme';
import type { ForecastRain } from '../../api/forecastService';
import type { Session } from '../../api/eventsService';
import './RainForecastsTimeline.css';

dayjs.extend(utc);
dayjs.extend(timezone);

interface RainForecastsTimelineProps {
  forecasts: ForecastRain[];
  sessions?: Session[];
  eventStartDate: string;  // YYYY-MM-DD
  eventEndDate: string;    // YYYY-MM-DD
  circuitTimeZone?: string;
  loading?: boolean;
  refreshing?: boolean; // For refresh button spinner during silent updates
  // Header props
  onRefresh?: () => void;
  onCreateForecast?: () => void;
  canManageForecasts?: boolean;
  isEventActive?: boolean;
  hasAdminRole?: boolean;
  error?: string | null;
  onClearError?: () => void;
}

// Rain status colors from legacy portal
const RAIN_COLORS = {
  green: '#2dd598',   // No rain expected
  orange: '#ff8e01',  // Rain expected
  red: '#ff3b3c',     // Currently raining
  yellow: '#e4e14d',  // Drops at times
};

// Status definitions for swim lanes
const STATUS_LANES: Array<{ status: 0 | 1 | 2 | 3; label: string; color: string }> = [
  { status: 0, label: 'No rain', color: RAIN_COLORS.green },
  { status: 3, label: 'Few drops', color: RAIN_COLORS.yellow },
  { status: 1, label: 'Expected', color: RAIN_COLORS.orange },
  { status: 2, label: 'Raining', color: RAIN_COLORS.red },
];

// Calculate rain status based on rain_type and few_drops
const getRainStatus = (rainType: number | null, fewDrops: boolean | null): 0 | 1 | 2 | 3 => {
  if (rainType === 0 && fewDrops) return 3;
  if (rainType === 2) return 2;
  if (rainType === 1) return 1;
  return 0;
};

const getStatusColor = (status: 0 | 1 | 2 | 3): string => {
  switch (status) {
    case 0: return RAIN_COLORS.green;
    case 1: return RAIN_COLORS.orange;
    case 2: return RAIN_COLORS.red;
    case 3: return RAIN_COLORS.yellow;
    default: return RAIN_COLORS.green;
  }
};

const getStatusLabel = (status: 0 | 1 | 2 | 3): string => {
  switch (status) {
    case 0: return 'No rain';
    case 1: return 'Rain expected';
    case 2: return 'Raining';
    case 3: return 'Few drops';
    default: return 'Unknown';
  }
};

// Get color with alpha channel
function getStatusColorWithAlpha(status: 0 | 1 | 2 | 3, alpha: number = 1): string {
  const color = getStatusColor(status);
  // Convert hex to rgba
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Group forecasts by their status for swim lane display
function groupForecastsByStatus(forecasts: ForecastRain[]): Map<0 | 1 | 2 | 3, ForecastRain[]> {
  const groups = new Map<0 | 1 | 2 | 3, ForecastRain[]>();
  
  // Initialize all status groups
  groups.set(0, []);
  groups.set(1, []);
  groups.set(2, []);
  groups.set(3, []);
  
  // Only include active forecasts
  const activeForecasts = forecasts.filter(f => f.is_active);
  
  for (const forecast of activeForecasts) {
    const status = getRainStatus(forecast.rain_type, forecast.few_drops);
    const group = groups.get(status)!;
    group.push(forecast);
  }
  
  return groups;
}

export const RainForecastsTimeline: React.FC<RainForecastsTimelineProps> = ({
  forecasts,
  sessions = [],
  eventStartDate,
  eventEndDate,
  circuitTimeZone,
  loading = false,
  refreshing = false,
  onRefresh,
  onCreateForecast,
  canManageForecasts = false,
  isEventActive = true,
  hasAdminRole = false,
  error,
  onClearError,
}) => {
  const { theme } = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [hoveredForecast, setHoveredForecast] = useState<ForecastRain | null>(null);
  const [hoveredSession, setHoveredSession] = useState<Session | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [canvasRenderKey, setCanvasRenderKey] = useState(0);
  
  // Group forecasts by status for swim lanes
  const forecastsByStatus = useMemo(() => groupForecastsByStatus(forecasts), [forecasts]);
  
  // Update current time every minute
  useEffect(() => {
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();
    
    const alignTimeout = setTimeout(() => {
      setCurrentTime(new Date());
      const interval = setInterval(() => {
        setCurrentTime(new Date());
      }, 60 * 1000);
      
      return () => clearInterval(interval);
    }, msUntilNextMinute);
    
    return () => clearTimeout(alignTimeout);
  }, []);
  
  // Re-draw canvas when container size changes (e.g. window resize)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      setCanvasRenderKey((prev) => prev + 1);
    });
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [loading, eventStartDate, eventEndDate]);
  
  // Get event time bounds
  const getEventBounds = useCallback(() => {
    const tz = circuitTimeZone || 'UTC';
    const startDate = dayjs.tz(eventStartDate, tz).startOf('day').toDate();
    const endDate = dayjs.tz(eventEndDate, tz).endOf('day').toDate();
    return { start: startDate, end: endDate };
  }, [eventStartDate, eventEndDate, circuitTimeZone]);
  
  // Convert date to pixel position
  const dateToX = useCallback((date: Date, bounds: { start: Date; end: Date }, width: number): number => {
    const totalDuration = bounds.end.getTime() - bounds.start.getTime();
    const elapsed = date.getTime() - bounds.start.getTime();
    return (elapsed / totalDuration) * width;
  }, []);
  
  // Layout constants
  const LABEL_WIDTH = 60; // Width for status labels on the left
  const LANE_HEIGHT = 18; // Height of each swim lane
  const LANE_PADDING = 2; // Padding between lanes
  const SESSION_LABEL_HEIGHT = 14; // Height for session labels at top
  const DATE_LABEL_HEIGHT = 22; // Height for date labels at bottom
  const TICK_WIDTH = 2; // Width of each forecast tick mark
  
  // Calculate canvas height based on swim lanes
  const canvasHeight = SESSION_LABEL_HEIGHT + (LANE_HEIGHT + LANE_PADDING) * STATUS_LANES.length + DATE_LABEL_HEIGHT;
  
  // Format the current time for display
  const currentTimeDisplay = useMemo(() => {
    const tz = circuitTimeZone || 'UTC';
    return dayjs(currentTime).tz(tz).format('MMM D, YYYY, HH:mm');
  }, [currentTime, circuitTimeZone]);
  
  // Draw timeline
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const bounds = getEventBounds();
    const { width, height } = canvas.getBoundingClientRect();
    
    // Set canvas resolution for retina displays
    canvas.width = width * window.devicePixelRatio;
    canvas.height = height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    
    const isDark = theme === 'dark';
    const timelineWidth = width - LABEL_WIDTH;
    const timelineLeft = LABEL_WIDTH;
    
    // Helper to convert date to X position within the timeline area
    const dateToTimelineX = (date: Date) => {
      return timelineLeft + dateToX(date, bounds, timelineWidth);
    };
    
    // Clear canvas with background color
    ctx.fillStyle = isDark ? '#141414' : '#ffffff';
    ctx.fillRect(0, 0, width, height);
    
    // Draw past time overlay
    const nowX = dateToTimelineX(currentTime);
    if (currentTime > bounds.start) {
      const pastEndX = Math.min(nowX, width);
      ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)';
      ctx.fillRect(timelineLeft, 0, pastEndX - timelineLeft, height - DATE_LABEL_HEIGHT);
    }
    
    const totalDays = (bounds.end.getTime() - bounds.start.getTime()) / (1000 * 60 * 60 * 24);
    const [startYear, startMonth, startDay] = eventStartDate.split('-').map(Number);
    
    // Draw quarter-day lines
    ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)';
    ctx.lineWidth = 1;
    
    for (let day = 0; day <= Math.ceil(totalDays); day++) {
      for (const hour of [6, 12, 18]) {
        const date = new Date(bounds.start.getTime() + (day * 24 + hour) * 60 * 60 * 1000);
        const x = dateToTimelineX(date);
        
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height - DATE_LABEL_HEIGHT);
        ctx.stroke();
      }
    }
    
    // Draw day boundary lines
    ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)';
    ctx.lineWidth = 1;
    ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)';
    ctx.font = '9px sans-serif';
    ctx.textAlign = 'center';
    
    for (let day = 0; day <= Math.ceil(totalDays); day++) {
      const date = new Date(bounds.start.getTime() + day * 24 * 60 * 60 * 1000);
      const x = dateToTimelineX(date);
      
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
      
      const labelDate = new Date(startYear, startMonth - 1, startDay + day);
      const dayOfWeek = labelDate.toLocaleDateString('en-US', { weekday: 'short' });
      const dateLabel = labelDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      ctx.fillText(dayOfWeek, x, height - 12);
      ctx.fillText(dateLabel, x, height - 2);
    }
    
    // Draw sessions as background shading
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'center';
    
    sessions.forEach(session => {
      const sessionStart = new Date(session.start_time);
      const sessionEnd = new Date(session.end_time);
      const startX = dateToTimelineX(sessionStart);
      const endX = dateToTimelineX(sessionEnd);
      
      // Session background
      ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)';
      ctx.fillRect(startX, SESSION_LABEL_HEIGHT, endX - startX, height - SESSION_LABEL_HEIGHT - DATE_LABEL_HEIGHT);
      
      // Session label at top
      ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)';
      ctx.fillText(session.session_name, (startX + endX) / 2, 10);
    });
    
    // Draw swim lane labels and backgrounds
    ctx.textAlign = 'right';
    ctx.font = '9px sans-serif';
    
    STATUS_LANES.forEach((lane, index) => {
      const laneY = SESSION_LABEL_HEIGHT + index * (LANE_HEIGHT + LANE_PADDING);
      
      // Lane background (alternating subtle shading)
      if (index % 2 === 0) {
        ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)';
        ctx.fillRect(timelineLeft, laneY, timelineWidth, LANE_HEIGHT);
      }
      
      // Lane label
      ctx.fillStyle = lane.color;
      ctx.fillText(lane.label, LABEL_WIDTH - 6, laneY + LANE_HEIGHT - 4);
      
      // Lane indicator dot
      ctx.beginPath();
      ctx.arc(LABEL_WIDTH - LABEL_WIDTH + 8, laneY + LANE_HEIGHT / 2, 3, 0, Math.PI * 2);
      ctx.fillStyle = lane.color;
      ctx.fill();
    });
    
    // Draw vertical separator line between labels and timeline
    ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(timelineLeft, 0);
    ctx.lineTo(timelineLeft, height - DATE_LABEL_HEIGHT);
    ctx.stroke();
    
    // Draw forecasts as thin tick marks in their respective lanes
    STATUS_LANES.forEach((lane, laneIndex) => {
      const laneY = SESSION_LABEL_HEIGHT + laneIndex * (LANE_HEIGHT + LANE_PADDING);
      const laneForecasts = forecastsByStatus.get(lane.status) || [];
      
      laneForecasts.forEach(forecast => {
        const forecastTime = new Date(forecast.value_date);
        const x = dateToTimelineX(forecastTime);
        
        // Draw tick mark
        ctx.fillStyle = lane.color;
        ctx.fillRect(x - TICK_WIDTH / 2, laneY + 1, TICK_WIDTH, LANE_HEIGHT - 2);
      });
    });
    
    // Draw NOW line
    if (currentTime >= bounds.start && currentTime <= bounds.end) {
      ctx.strokeStyle = isDark ? 'rgba(255, 77, 79, 0.8)' : 'rgba(255, 77, 79, 0.9)';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(nowX, 0);
      ctx.lineTo(nowX, height - DATE_LABEL_HEIGHT);
      ctx.stroke();
      ctx.setLineDash([]);
      
      // NOW label
      ctx.fillStyle = isDark ? 'rgba(255, 77, 79, 0.9)' : 'rgba(255, 77, 79, 1)';
      ctx.font = 'bold 8px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('NOW', nowX, 8);
    }
    
  }, [forecasts, forecastsByStatus, sessions, currentTime, theme, getEventBounds, dateToX, eventStartDate, canvasRenderKey]);
  
  // Check if mouse position is over a session
  const getSessionAtPosition = useCallback((x: number, y: number, bounds: { start: Date; end: Date }, width: number, height: number): Session | null => {
    const timelineWidth = width - LABEL_WIDTH;
    const timelineLeft = LABEL_WIDTH;
    
    if (x < timelineLeft || y > height - DATE_LABEL_HEIGHT) return null;
    
    for (const session of sessions) {
      const startX = timelineLeft + dateToX(new Date(session.start_time), bounds, timelineWidth);
      const endX = timelineLeft + dateToX(new Date(session.end_time), bounds, timelineWidth);
      
      if (x >= startX && x <= endX) {
        return session;
      }
    }
    
    return null;
  }, [sessions, dateToX]);
  
  // Handle mouse move for hover detection
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const bounds = getEventBounds();
    const timelineWidth = rect.width - LABEL_WIDTH;
    const timelineLeft = LABEL_WIDTH;
    
    // Check if hovering over any forecast
    let foundForecast: ForecastRain | null = null;
    
    // Determine which lane the mouse is in
    const laneIndex = Math.floor((y - SESSION_LABEL_HEIGHT) / (LANE_HEIGHT + LANE_PADDING));
    
    if (laneIndex >= 0 && laneIndex < STATUS_LANES.length && x >= timelineLeft) {
      const lane = STATUS_LANES[laneIndex];
      const laneForecasts = forecastsByStatus.get(lane.status) || [];
      
      // Find forecast near this x position (within 5px tolerance)
      const tolerance = 5;
      
      for (const forecast of laneForecasts) {
        const forecastTime = new Date(forecast.value_date);
        const forecastX = timelineLeft + dateToX(forecastTime, bounds, timelineWidth);
        
        if (Math.abs(x - forecastX) <= tolerance) {
          foundForecast = forecast;
          break;
        }
      }
    }
    
    if (foundForecast) {
      setHoveredForecast(foundForecast);
      setHoveredSession(null);
      setTooltipPosition({ x: e.clientX, y: e.clientY - 120 });
    } else {
      setHoveredForecast(null);
      
      const session = getSessionAtPosition(x, y, bounds, rect.width, rect.height);
      
      if (session) {
        setHoveredSession(session);
        setTooltipPosition({ x: e.clientX, y: e.clientY - 80 });
      } else {
        setHoveredSession(null);
        setTooltipPosition(null);
      }
    }
  }, [forecastsByStatus, getEventBounds, dateToX, getSessionAtPosition]);
  
  const handleMouseLeave = useCallback(() => {
    setHoveredForecast(null);
    setHoveredSession(null);
    setTooltipPosition(null);
  }, []);
  
  // Format time for tooltip display
  const formatTooltipTime = useCallback((timestamp: string) => {
    const tz = circuitTimeZone || 'UTC';
    return dayjs(timestamp).tz(tz).format('MMM D, HH:mm');
  }, [circuitTimeZone]);
  
  if (loading && forecasts.length === 0) {
    return (
      <Card styles={{ body: { padding: '8px 12px' } }}>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Spin size="small" />
        </div>
      </Card>
    );
  }
  
  if (!eventStartDate || !eventEndDate) {
    return (
      <Card styles={{ body: { padding: '8px 12px' } }}>
        <AntAlert title="No event selected" description="Please select an event to view rain forecasts." type="info" showIcon />
      </Card>
    );
  }
  
  return (
    <Card 
      styles={{ body: { padding: '8px 12px' } }}
      className="rain-forecasts-timeline-card"
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span>Rain Forecasts Timeline</span>
            <span style={{ 
              fontSize: '12px', 
              fontWeight: 'normal',
              color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.65)',
              backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              padding: '2px 10px',
              borderRadius: '4px',
            }}>
              {currentTimeDisplay}
              {circuitTimeZone && (
                <span style={{ 
                  color: theme === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
                  marginLeft: 6,
                }}>
                  ({circuitTimeZone})
                </span>
              )}
            </span>
            {!isEventActive && hasAdminRole && !canManageForecasts && (
              <Tag color="default">Event ended - Read only</Tag>
            )}
            {!isEventActive && hasAdminRole && canManageForecasts && (
              <Tag color="green">Demo — editing allowed</Tag>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: 16 }}>
            {onRefresh && (
              <Tooltip title="Refresh">
                <Button
                  icon={<ReloadOutlined spin={refreshing} />}
                  onClick={onRefresh}
                  size="small"
                  type="text"
                />
              </Tooltip>
            )}
            {canManageForecasts && onCreateForecast && (
              <Tooltip title="Add Rain Forecast">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={onCreateForecast}
                  size="small"
                />
              </Tooltip>
            )}
          </div>
        </div>
      }
    >
      {error && (
        <AntAlert
          title="Error"
          description={error}
          type="error"
          closable={{ onClose: onClearError }}
          style={{ marginBottom: 8 }}
        />
      )}
      
      {/* Canvas */}
      <div 
        ref={containerRef}
        className="rain-forecasts-timeline-container"
        style={{ height: canvasHeight }}
      >
        <canvas
          ref={canvasRef}
          className="rain-forecasts-timeline-canvas"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        />
        
        {/* Forecast Tooltip */}
        {hoveredForecast && tooltipPosition && (
          <div
            className={`rain-forecasts-timeline-tooltip ${theme === 'dark' ? 'dark' : 'light'}`}
            style={{
              left: tooltipPosition.x,
              top: tooltipPosition.y,
            }}
          >
            <div className="tooltip-header">
              <span 
                className="tooltip-status"
                style={{ 
                  backgroundColor: getStatusColorWithAlpha(getRainStatus(hoveredForecast.rain_type, hoveredForecast.few_drops), 0.2),
                  color: getStatusColor(getRainStatus(hoveredForecast.rain_type, hoveredForecast.few_drops)),
                  borderColor: getStatusColor(getRainStatus(hoveredForecast.rain_type, hoveredForecast.few_drops)),
                }}
              >
                {getStatusLabel(getRainStatus(hoveredForecast.rain_type, hoveredForecast.few_drops)).toUpperCase()}
              </span>
              <span className="tooltip-session">{hoveredForecast.session_type || 'All Sessions'}</span>
            </div>
            <div className="tooltip-message">{hoveredForecast.message}</div>
            <div className="tooltip-time">
              <span>Time: {formatTooltipTime(hoveredForecast.value_date)}</span>
              {hoveredForecast.rain_intensity !== null && (
                <span>Intensity: Level {hoveredForecast.rain_intensity}</span>
              )}
            </div>
            {(hoveredForecast.rain_start_time || hoveredForecast.rain_end_time) && (
              <div className="tooltip-rain-times">
                {hoveredForecast.rain_start_time && <span>Rain Start: {hoveredForecast.rain_start_time}</span>}
                {hoveredForecast.rain_end_time && <span>Rain End: {hoveredForecast.rain_end_time}</span>}
              </div>
            )}
          </div>
        )}
        
        {/* Session Tooltip */}
        {hoveredSession && tooltipPosition && !hoveredForecast && (() => {
          const tz = circuitTimeZone || 'UTC';
          const formatCalendarDate = (dateStr: string, includeDate: boolean = true) => {
            const dateInEventTZ = dayjs.utc(dateStr).tz(tz);
            if (includeDate) {
              return dateInEventTZ.format('MMM D, HH:mm');
            } else {
              return dateInEventTZ.format('HH:mm');
            }
          };
          
          return (
            <div
              style={{
                position: 'fixed',
                left: tooltipPosition.x,
                top: tooltipPosition.y,
                transform: 'translateX(-50%)',
                backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                color: theme === 'dark' ? '#fff' : '#000',
                padding: '8px 12px',
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'}`,
                pointerEvents: 'none',
                zIndex: 1000,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                maxWidth: '300px',
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{hoveredSession.session_name}</div>
              <div style={{ opacity: 0.8, marginBottom: hoveredSession.description ? '4px' : '0' }}>
                {formatCalendarDate(hoveredSession.start_time, true)}
                {' - '}
                {formatCalendarDate(hoveredSession.end_time, false)}
              </div>
              {hoveredSession.description && (
                <div style={{ 
                  opacity: 0.7, 
                  fontSize: '11px', 
                  whiteSpace: 'normal',
                  lineHeight: '1.4'
                }}>
                  {hoveredSession.description}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </Card>
  );
};
